import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import firebaseService from '../../services/firebaseService';

const inputClass = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-400 focus:border-transparent outline-none transition-all bg-gray-50";
const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const { signInWithGoogle, signUpWithEmail, signInWithEmail, authError, user, hasPurchased, loading } = useAuth();
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState('choice');
  const [formData, setFormData] = useState({ email: '', password: '', displayName: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  React.useEffect(() => {
    // Wait until the profile has loaded, otherwise a paid user briefly looks unpaid
    if (user && isOpen && !loading) {
      if (hasPurchased) {
        onClose();
        navigate('/reader-pageflip');
      } else {
        if (onLoginSuccess) onLoginSuccess();
        else onClose();
      }
    }
  }, [user, hasPurchased, loading, isOpen, onClose, onLoginSuccess, navigate]);

  const handleGoogleSignIn = async () => {
    if (!termsAccepted) { setError('Please agree to the Terms & Conditions to continue.'); return; }
    setIsLoading(true); setError('');
    try {
      const result = await signInWithGoogle();
      if (!result.success) setError(result.error);
    } catch { setError('Google sign-in failed. Please try again.'); }
    finally { setIsLoading(false); }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    if (!termsAccepted) { setError('Please agree to the Terms & Conditions to continue.'); return; }
    setIsLoading(true); setError('');
    try {
      const result = await signUpWithEmail(formData.email, formData.password, formData.displayName);
      if (!result.success) setError(result.error);
    } catch { setError('Sign-up failed. Please try again.'); }
    finally { setIsLoading(false); }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault(); setIsLoading(true); setError('');
    try {
      const result = await signInWithEmail(formData.email, formData.password);
      if (!result.success) setError(result.error);
    } catch { setError('Sign-in failed. Please try again.'); }
    finally { setIsLoading(false); }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const resetForm = () => {
    setFormData({ email: '', password: '', displayName: '' });
    setError(''); setAuthMode('choice'); setForgotPasswordSuccess(false); setTermsAccepted(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!formData.email) { setError('Please enter your email address.'); return; }
    setIsLoading(true); setError('');
    try {
      const result = await firebaseService.sendPasswordReset(formData.email);
      if (result.success) { setForgotPasswordSuccess(true); setError(''); }
      else setError(result.error || 'Failed to send password reset email.');
    } catch { setError('Failed to send password reset email. Please try again.'); }
    finally { setIsLoading(false); }
  };

  const handleClose = () => { resetForm(); onClose(); };

  if (!isOpen) return null;

  const termsCheckbox = (
    <label className="flex items-start gap-2.5 text-xs text-gray-600 cursor-pointer select-none">
      <input type="checkbox" checked={termsAccepted}
        onChange={(e) => { setTermsAccepted(e.target.checked); setError(''); }}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 accent-red-600 cursor-pointer" />
      <span>
        I have read and agree to the{' '}
        <a href="/legal#terms-conditions" target="_blank" rel="noopener noreferrer"
          className="text-red-500 hover:text-red-600 hover:underline font-medium">
          Terms &amp; Conditions
        </a>{' '}and{' '}
        <a href="/legal#privacy-policy" target="_blank" rel="noopener noreferrer"
          className="text-red-500 hover:text-red-600 hover:underline font-medium">
          Privacy Policy
        </a>.
      </span>
    </label>
  );

  const titles = {
    choice: { title: 'Welcome to Fijoli', sub: 'Sign in or create an account to get started' },
    signup: { title: 'Create Account', sub: 'Join thousands of Fijoli readers' },
    signin: { title: 'Sign In', sub: 'Good to have you back!' },
    forgot: { title: 'Reset Password', sub: 'We\'ll send a reset link to your email' },
  };
  const { title, sub } = titles[authMode] || titles.choice;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
        />
        <motion.div
          initial={{ opacity: 0, y: 60, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 60, scale: 0.97 }}
          transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
          className="relative w-full sm:max-w-md bg-white sm:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden"
          style={{ maxHeight: '92vh', overflowY: 'auto' }}
        >
          {/* Header */}
          <div className="relative px-6 pt-7 pb-5 text-white"
            style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }}>
            <button onClick={handleClose}
              className="absolute top-4 right-4 p-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors focus:outline-none"
              aria-label="Close">
              <XMarkIcon className="h-5 w-5 text-white" />
            </button>
            <div className="w-10 h-1 bg-white/30 rounded-full mx-auto mb-5 sm:hidden" />
            {/* Back button */}
            {authMode !== 'choice' && (
              <button onClick={() => { setAuthMode('choice'); setError(''); }}
                className="flex items-center gap-1 text-xs text-red-200 hover:text-white mb-3 transition-colors">
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            )}
            <h2 className="text-xl font-bold mb-0.5">{title}</h2>
            <p className="text-sm text-red-200">{sub}</p>
          </div>

          {/* Body */}
          <div className="px-5 py-5">
            {/* Choice screen */}
            {authMode === 'choice' && (
              <div className="space-y-3">
                {termsCheckbox}

                {/* Google */}
                <button onClick={handleGoogleSignIn} disabled={isLoading || !termsAccepted}
                  className="flex w-full items-center justify-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50 transition-all disabled:opacity-50 active:scale-[0.98]">
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {isLoading ? 'Signing in...' : 'Continue with Google'}
                </button>

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs text-gray-400">or</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                <button onClick={() => setAuthMode('signup')} disabled={!termsAccepted}
                  className="w-full py-3 rounded-2xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors shadow-md disabled:opacity-50 active:scale-[0.98]">
                  Create New Account
                </button>
                <button onClick={() => setAuthMode('signin')}
                  className="w-full py-3 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-800 text-sm font-semibold transition-colors active:scale-[0.98]">
                  Sign In to Existing Account
                </button>
              </div>
            )}

            {/* Sign Up */}
            {authMode === 'signup' && (
              <form onSubmit={handleEmailSignUp} className="space-y-4">
                <div>
                  <label className={labelClass}>Full Name</label>
                  <input type="text" name="displayName" id="displayName" required
                    value={formData.displayName} onChange={handleChange}
                    placeholder="Enter your full name" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Email Address</label>
                  <input type="email" name="email" id="email" required
                    value={formData.email} onChange={handleChange}
                    placeholder="Enter your email" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Password</label>
                  <input type="password" name="password" id="password" required
                    value={formData.password} onChange={handleChange}
                    placeholder="Min. 6 characters" minLength={6} className={inputClass} />
                </div>
                {termsCheckbox}
                <button type="submit" disabled={isLoading || !termsAccepted}
                  className="w-full py-3.5 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-semibold text-sm transition-colors shadow-md disabled:opacity-50 active:scale-[0.98]">
                  {isLoading ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>
            )}

            {/* Sign In */}
            {authMode === 'signin' && (
              <form onSubmit={handleEmailSignIn} className="space-y-4">
                <div>
                  <label className={labelClass}>Email Address</label>
                  <input type="email" name="email" id="email" required
                    value={formData.email} onChange={handleChange}
                    placeholder="Enter your email" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Password</label>
                  <input type="password" name="password" id="password" required
                    value={formData.password} onChange={handleChange}
                    placeholder="Enter your password" className={inputClass} />
                  <button type="button" onClick={() => setAuthMode('forgot')}
                    className="mt-1.5 text-xs text-red-500 hover:text-red-600 hover:underline float-right">
                    Forgot Password?
                  </button>
                </div>
                <div className="pt-1">
                  <button type="submit" disabled={isLoading}
                    className="w-full py-3.5 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-semibold text-sm transition-colors shadow-md disabled:opacity-50 active:scale-[0.98]">
                    {isLoading ? 'Signing In...' : 'Sign In'}
                  </button>
                </div>
              </form>
            )}

            {/* Forgot Password */}
            {authMode === 'forgot' && (
              <div className="space-y-4">
                {forgotPasswordSuccess ? (
                  <div className="text-center py-4">
                    <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <svg className="h-7 w-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-base font-bold text-gray-900 mb-1">Check Your Inbox</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Reset link sent to <strong>{formData.email}</strong>
                    </p>
                    <button onClick={() => setAuthMode('choice')}
                      className="w-full py-3 rounded-2xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors">
                      Back to Sign In
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <label className={labelClass}>Email Address</label>
                      <input type="email" name="email" id="forgot-email" required
                        value={formData.email} onChange={handleChange}
                        placeholder="Enter your email" className={inputClass} />
                    </div>
                    <button type="submit" disabled={isLoading}
                      className="w-full py-3.5 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-semibold text-sm transition-colors shadow-md disabled:opacity-50">
                      {isLoading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* Error */}
            {(error || authError) && (
              <div className="mt-4 text-xs text-red-700 bg-red-50 border border-red-100 px-4 py-3 rounded-xl">
                {error || authError}
              </div>
            )}

            {/* Footer */}
            {(authMode === 'signin' || authMode === 'forgot') && (
            <p className="mt-5 text-center text-xs text-gray-400">
              By continuing, you agree to our{' '}
              <a href="/legal" target="_blank" rel="noopener noreferrer"
                className="text-red-500 hover:text-red-600 hover:underline font-medium">
                Terms of Service &amp; Privacy Policy
              </a>.
            </p>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default LoginModal;
